import { useCallback, useEffect, useRef, useState } from 'react';
import { components } from '@apiSchema';
import http from '@core/fetch';

type MenuExamplesResponse = components['schemas']['SnapMacMenuExamplesResponse'];
type MenuExampleDto = components['schemas']['SnapMacMenuExampleItem'];

export const MAC_MENU_EXAMPLES_PAGE_SIZE = 100;

const REQUEST_TYPE = 'mac menu examples';

export type MacMenuExampleSortKey = 'item' | 'price' | 'venue';
export type MacMenuExampleSortDirection = 'asc' | 'desc';

// The API's sort enums are unnamed numbers; values follow MT (macMenuExamples.ts): 1 Venue, 2 Item, 4 Price,
// 0 ascending / 1 descending. Description has no server sort.
const SORT_COLUMNS: Record<MacMenuExampleSortKey, 1 | 2 | 4> = { venue: 1, item: 2, price: 4 };
const SORT_DIRECTIONS: Record<MacMenuExampleSortDirection, 0 | 1> = { asc: 0, desc: 1 };

export interface MacMenuExamplesQuery {
    search: string;
    sortKey: MacMenuExampleSortKey;
    direction: MacMenuExampleSortDirection;
}

export const DEFAULT_MAC_MENU_EXAMPLES_QUERY: MacMenuExamplesQuery = { search: '', sortKey: 'item', direction: 'asc' };

export interface IMacMenuExample {
    id: string;
    item: string;
    description: string;
    price: string;
    venue: string;
}

interface MenuExamplesPage {
    items: IMacMenuExample[];
    totalRecords: number;
}

function mapMenuExample(dto: MenuExampleDto, index: number, skip: number): IMacMenuExample {
    return {
        id: `${dto.menuItemId ?? 'item'}-${skip + index}`,
        item: dto.menuItemName ?? '',
        description: dto.description ?? '',
        price: dto.price ?? '',
        venue: dto.restaurantName ?? '',
    };
}

export async function getMacMenuExamples(
    macId: number,
    keywordId: number,
    { search, sortKey, direction }: MacMenuExamplesQuery,
    skip: number,
): Promise<MenuExamplesPage> {
    // postXHR rejects on network errors and non-2xx statuses, so a failed page never leaves the loader on.
    const response: MenuExamplesResponse | null = await http.postXHR(
        REQUEST_TYPE,
        { id: macId, keywordId },
        JSON.stringify({
            tableParameters: {
                search,
                sortColumn: SORT_COLUMNS[sortKey],
                sortDir: SORT_DIRECTIONS[direction],
                skip,
                take: MAC_MENU_EXAMPLES_PAGE_SIZE,
            },
        }),
    );

    return {
        items: (response?.data ?? []).map((dto, index) => mapMenuExample(dto, index, skip)),
        totalRecords: response?.totalRecords ?? 0,
    };
}

export function useMacMenuExamples(
    macId: number,
    keywordId: number | null,
    { search, sortKey, direction }: MacMenuExamplesQuery = DEFAULT_MAC_MENU_EXAMPLES_QUERY,
) {
    const [items, setItems] = useState<IMacMenuExample[]>([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const requestIdRef = useRef(0);

    const load = useCallback(async (skip: number) => {
        if (keywordId === null) {
            return;
        }
        const requestId = ++requestIdRef.current;
        setIsLoading(true);
        try {
            const page = await getMacMenuExamples(macId, keywordId, { search, sortKey, direction }, skip);
            if (requestId !== requestIdRef.current) {
                return;
            }
            setItems((current) => (skip === 0 ? page.items : [...current, ...page.items]));
            setTotalRecords(page.totalRecords);
        } catch (err) {
            console.error('Fetch error:', REQUEST_TYPE, err);
        } finally {
            if (requestId === requestIdRef.current) {
                setIsLoading(false);
            }
        }
    }, [macId, keywordId, search, sortKey, direction]);

    useEffect(() => {
        const requestCounter = requestIdRef;
        setItems([]);
        setTotalRecords(0);
        load(0);

        return () => {
            // Invalidate the in-flight request so a slow response for the previous keyword/query is ignored.
            requestCounter.current++;
        };
    }, [load]);

    const loadMore = () => load(items.length);

    return {
        items,
        totalRecords,
        isLoading,
        hasMore: items.length < totalRecords,
        loadMore,
    };
}
